import { useEffect, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ReactGA from "react-ga4";
import { LuCopy } from "react-icons/lu";
import { useProjectId } from "../../../core_stores/useProjectStore";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { useProjectPreviewUrl } from "../../../view_stores/useProjectPreviewStore";

interface HttpLogEntry {
  timestamp: string;
  method: string;
  url: string;
  status: number;
}

export function ProjectDebug() {
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", title: "Workspace Debug" });
  }, []);

  return (
    <div className="flex flex-col gap-8 p-4">
      <ProjectId />
      <PreviewUrl />
      <ErrorTesting />
      <Logs />
    </div>
  );
}

function ProjectId() {
  const projectId = useProjectId();
  const { showSnackbar } = useSnackbar();

  const handleCopy = () => {
    if (projectId) {
      showSnackbar("Project ID copied to clipboard");
    } else {
      showSnackbar("No project ID available", {
        type: "error",
      });
    }
  };

  return (
    <div className="w-full">
      <label
        htmlFor="sessionId"
        className="block text-base font-medium text-light-text dark:text-dark-text mb-2"
      >
        Project ID
      </label>
      <div className="flex w-full">
        <div
          className="flex-1 px-4 py-2.5 
          bg-light-surface-tertiary dark:bg-[#191C1F] 
          text-light-text dark:text-dark-text
          rounded-lg rounded-r-none"
        >
          {projectId ?? "unknown"}
        </div>
        <CopyToClipboard text={projectId ?? ""} onCopy={handleCopy}>
          <button
            disabled={!projectId}
            className="px-4 py-2.5 
            bg-light-surface-tertiary dark:bg-[#191C1F] 
            text-light-text dark:text-dark-text
            disabled:opacity-50 disabled:cursor-not-allowed
            rounded-r-lg focus:outline-none
            transition-colors duration-300
            [@media(hover:hover)]:hover:bg-light-hover [@media(hover:hover)]:dark:hover:bg-black"
            title="Copy Project ID"
          >
            <LuCopy size={16} />
          </button>
        </CopyToClipboard>
      </div>
    </div>
  );
}

function PreviewUrl() {
  const previewUrl = useProjectPreviewUrl();
  const { showSnackbar } = useSnackbar();

  const handleCopy = () => {
    if (previewUrl) {
      showSnackbar("Preview URL copied to clipboard");
    } else {
      showSnackbar("No preview URL available", {
        type: "error",
      });
    }
  };

  return (
    <div className="w-full">
      <label
        htmlFor="sessionId"
        className="block text-base font-medium text-light-text dark:text-dark-text mb-2"
      >
        Preview URL
      </label>
      <div className="flex w-full">
        <div
          className="flex-1 px-4 py-2.5 
          bg-light-surface-tertiary dark:bg-[#191C1F] 
          text-light-text dark:text-dark-text
          rounded-lg rounded-r-none"
        >
          {previewUrl ?? "unknown"}
        </div>
        <CopyToClipboard text={previewUrl ?? ""} onCopy={handleCopy}>
          <button
            disabled={!previewUrl}
            className="px-4 py-2.5 
            bg-light-surface-tertiary dark:bg-[#191C1F] 
            text-light-text dark:text-dark-text
            disabled:opacity-50 disabled:cursor-not-allowed
            rounded-r-lg focus:outline-none
            transition-colors duration-300
            [@media(hover:hover)]:hover:bg-light-hover [@media(hover:hover)]:dark:hover:bg-black"
            title="Copy Preview URL"
          >
            <LuCopy size={16} />
          </button>
        </CopyToClipboard>
      </div>
    </div>
  );
}

function ErrorTesting() {
  const handleSentryTest = () => {
      throw new Error('Sentry test crash');
  };

  return (
    <div className="w-full">
      <label className="block text-base font-medium text-light-text dark:text-dark-text mb-2">
        Error Testing
      </label>
      <button
        onClick={handleSentryTest}
        className="px-4 py-2.5 
        bg-red-500 hover:bg-red-600 
        text-white
        rounded-lg focus:outline-none
        transition-colors duration-300
        font-medium"
        title="Throw Sentry Test Error"
      >
        Throw Sentry Test Error
      </button>
    </div>
  );
}

function Logs() {
  const [httpLogs] = useState<HttpLogEntry[]>([
    {
      timestamp: "2024-03-20 14:30:45",
      method: "GET",
      url: "/api/projects",
      status: 200,
    },
    {
      timestamp: "2024-03-20 14:30:42",
      method: "POST",
      url: "/api/projects/123/update",
      status: 201,
    },
    {
      timestamp: "2024-03-20 14:30:40",
      method: "GET",
      url: "/api/user/profile",
      status: 200,
    },
  ]);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <span className="text-base font-medium text-light-text dark:text-dark-text">
          HTTP Logs
        </span>
        <button
          className="bg-light-surface-tertiary dark:bg-[#191C1F] 
          text-light-text dark:text-dark-text
          disabled:opacity-50 disabled:cursor-not-allowed
          rounded-lg px-4 py-2 flex items-center gap-2
          [@media(hover:hover)]:hover:bg-light-hover [@media(hover:hover)]:dark:hover:bg-black"
        >
          <LuCopy size={16} />
          Copy All
        </button>
      </div>
      <div
        className="rounded-lg overflow-hidden w-full
      bg-light-surface-tertiary dark:bg-[#191C1F] 
          text-light-text dark:text-dark-text"
      >
        <div className="overflow-x-auto max-h-[400px] p-4 font-mono text-sm">
          {httpLogs.map((log, index) => (
            <HttpLog key={index} index={index} log={log} />
          ))}
        </div>
      </div>
    </div>
  );
}

function HttpLog({ index, log }: { index: number; log: HttpLogEntry }) {
  return (
    <div key={index} className="mb-2">
      <span>[{log.timestamp}]</span>
      <span
        className={`mx-2 ${
          log.method === "GET"
            ? "text-blue-500"
            : log.method === "POST"
            ? "text-green-500"
            : "text-light-placeholder dark:text-dark-placeholder"
        }`}
      >
        {log.method}
      </span>
      <span className="text-light-placeholder dark:text-dark-placeholder">
        {log.url}
      </span>
      <span
        className={`ml-2 ${
          log.status >= 200 && log.status < 300
            ? "text-green-500"
            : log.status >= 400
            ? "text-red-500"
            : "text-yellow-500"
        }`}
      >
        {log.status}
      </span>
    </div>
  );
}
